// src/services/UpdatesService.ts
import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    Timestamp,
    limit,
    startAfter,
    DocumentSnapshot,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';
import {
    DatabaseUpdate,
    UpdateWriteData,
    UpdateCategory,
    DEFAULT_CATEGORY,
} from '../types/updates';
import { sanitiseHtml } from '../utils/sanitiseHtml';

const UPDATES_COLLECTION = 'updates';

/**
 * News items.
 *
 * ONE FIELD DECIDES VISIBILITY. `publishedAt` is null for a draft, a future time for something
 * scheduled and a past time once it is live, so the public feed is a single range query —
 * `publishedAt <= now`, ordered by the same field, which Firestore serves from one index and
 * `firestore.rules` can check without knowing anything else about the document.
 *
 * CONTENT IS SANITISED ON THE WAY IN, not on the way out. The landing page renders this HTML
 * to every visitor with `dangerouslySetInnerHTML`; sanitising at render would leave the stored
 * value dirty and rely on every future consumer remembering to clean it.
 *
 * LEGACY DOCUMENTS. Anything written before this schema has no `publishedAt`, `category` or
 * `createdByName`. `tools/migrations/upgrade-updates-schema.mjs` fills them in; `normalise`
 * below is the belt to that braces, so a document the migration missed still renders instead
 * of throwing.
 */

const normalise = (id: string, raw: Record<string, any>): DatabaseUpdate => ({
    id,
    title: raw.title ?? '',
    content: raw.content ?? '',
    category: (raw.category ?? DEFAULT_CATEGORY) as UpdateCategory,
    // A pre-schema document was, by definition, already public.
    publishedAt: raw.publishedAt ?? raw.createdAt ?? null,
    newUntil: raw.newUntil ?? null,
    releaseVersion: raw.releaseVersion ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt ?? raw.createdAt,
    createdBy: raw.createdBy ?? '',
    createdByName: raw.createdByName ?? 'Tundmatu',
    updatedBy: raw.updatedBy,
    updatedByName: raw.updatedByName,
});

const writePayload = (data: UpdateWriteData) => ({
    title: data.title.trim(),
    content: sanitiseHtml(data.content),
    category: data.category,
    publishedAt: data.publishedAt ? Timestamp.fromDate(data.publishedAt) : null,
    newUntil: data.newUntil ? Timestamp.fromDate(data.newUntil) : null,
    releaseVersion: data.releaseVersion?.trim() || null,
});

export const createUpdate = async (
    data: UpdateWriteData,
    author: { uid: string; name: string }
): Promise<string> => {
    const now = Timestamp.now();
    const ref = await addDoc(collection(firestore, UPDATES_COLLECTION), {
        ...writePayload(data),
        createdAt: now,
        updatedAt: now,
        createdBy: author.uid,
        createdByName: author.name,
    });
    return ref.id;
};

export const updateUpdate = async (
    id: string,
    data: UpdateWriteData,
    author: { uid: string; name: string }
): Promise<void> => {
    await updateDoc(doc(firestore, UPDATES_COLLECTION, id), {
        ...writePayload(data),
        updatedAt: Timestamp.now(),
        updatedBy: author.uid,
        updatedByName: author.name,
    });
};

export const deleteUpdate = async (id: string): Promise<void> => {
    await deleteDoc(doc(firestore, UPDATES_COLLECTION, id));
};

/** Everything, in every state. Admin only — the rules refuse this to anyone else. */
export const getAllUpdatesForAdmin = async (): Promise<DatabaseUpdate[]> => {
    const snap = await getDocs(
        query(collection(firestore, UPDATES_COLLECTION), orderBy('createdAt', 'desc'))
    );
    return snap.docs.map(d => normalise(d.id, d.data()));
};

/**
 * The public feed: published, in date order, newest first.
 *
 * The `<= now` bound is what hides a scheduled item, and it is evaluated against the CLIENT's
 * clock. That is enough to keep scheduled news out of the feed, and `firestore.rules` refuses
 * a document with no `publishedAt` at all — so a draft is genuinely private. An item scheduled
 * for the future is not equally private: it is published-but-not-yet-due, and a crafted query
 * could still fetch it. Making that airtight needs a scheduled function to flip the field
 * server-side; for patch notes it has not been worth one.
 */
export const getUpdatesForPublic = async (
    pageSize: number = 5,
    lastDoc?: DocumentSnapshot,
    category?: UpdateCategory | 'all'
): Promise<{ updates: DatabaseUpdate[]; hasMore: boolean; lastDoc?: DocumentSnapshot }> => {
    const clauses: any[] = [where('publishedAt', '<=', Timestamp.now())];
    if (category && category !== 'all') clauses.push(where('category', '==', category));

    let q = query(
        collection(firestore, UPDATES_COLLECTION),
        ...clauses,
        orderBy('publishedAt', 'desc'),
        limit(pageSize)
    );
    if (lastDoc) q = query(q, startAfter(lastDoc));

    const snap = await getDocs(q);
    return {
        updates: snap.docs.map(d => normalise(d.id, d.data())),
        hasMore: snap.docs.length === pageSize,
        lastDoc: snap.docs[snap.docs.length - 1],
    };
};
